import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../../hooks/useDebounce";
import useI18n from "../../../../../../hooks/useTranslations";
import { SearchAdminItem } from "../../../../../../http/types/items";
import { useItemsApi } from "../../../../../../http/useItems";
import { useShuttlesApi } from "../../../../../../http/vertigrip/useShuttles";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import ModalDialog from "../../../../../ui/ModalDialog";
import Numeric from "../../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../../ui/select/CustomSelect";

interface ShuttleForm {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

const CreateShuttleModalDialogButton: FC = () => {
  const i18n = useI18n();
  const { systemTypeId } = useParams<{ systemTypeId: string }>();
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const itemsApi = useItemsApi();
  const shuttlesApi = useShuttlesApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  return (
    <>
      <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.shuttles.addItemAsVertigrip}>
          <Form<ShuttleForm>
            defaultValues={{ item: null, order: 50, visibility: "hidden" }}
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => setShowNewItemDialog(false) }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={() => (formData) =>
              shuttlesApi
                .insertAdminShuttle({
                  itemId: formData.item?.id!,
                  order: formData.order!,
                  systemTypeId: systemTypeId!,
                  visibility: formData.visibility,
                })
                .then(() => setShowNewItemDialog(false))
                .catch((err) => {
                  dispatch(showErrorNotification(i18n, err));
                  setIsSubmitting(false);
                })
            }
          >
            {({ control, setValue }) => {
              return (
                <>
                  <Controller
                    name="item"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => {
                      return (
                        <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                          {({ labelId }) => {
                            const searchTerm = useRef("");
                            const aborter = useRef(new AbortController());

                            const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                            // Fetch initial data on mount
                            useEffect(() => {
                              loadOptions(searchTerm.current).then(setDefaultOptions);
                            }, []);

                            return (
                              <CustomAsyncSelect
                                {...field}
                                inputId={labelId}
                                defaultOptions={defaultOptions}
                                loadOptions={loadOptions}
                                isClearable
                                getOptionLabel={(x) => x.code}
                                getOptionValue={(x) => x.id}
                                onChange={(value) => setValue("item", value ?? null)}
                              />
                            );
                          }}
                        </FormField>
                      );
                    }}
                  />
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                        {({ labelId }) => (
                          <VisibilityScope
                            {...field}
                            inputId={labelId}
                            onChange={(value) => setValue("visibility", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="order"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.order} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            decimalScale={0}
                            allowNegative={false}
                            onBlur={(value) => setValue("order", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default CreateShuttleModalDialogButton;
